import { createJSONStorage } from "jotai/utils";

/**
 * localStorage debounce-write : evite ~60 writes/s pendant un resize de panel.
 *
 * Pattern :
 *   - Buffer en memoire pour les writes en flight
 *   - 200 ms apres le dernier write d'une key, flush vers localStorage
 *   - getItem retourne le buffer si present (read-after-write coherent)
 *   - flush sur beforeunload + pagehide pour ne pas perdre les writes en flight
 *
 * Partage : un seul Map global pour toutes les keys (chaque key a son propre
 * timer + valeur). Pas d'effet de bord cross-key.
 */

const DEBOUNCE_MS = 200;

interface PendingWrite {
  timer: ReturnType<typeof setTimeout>;
  value: string;
}

const pending = new Map<string, PendingWrite>();

function flushKey(base: Storage, key: string): void {
  const entry = pending.get(key);
  if (!entry) return;
  clearTimeout(entry.timer);
  try {
    base.setItem(key, entry.value);
  } catch {
    // QuotaExceeded etc. — silent fail (caller doesn't expect throws from storage)
  }
  pending.delete(key);
}

function flushAll(base: Storage): void {
  for (const key of [...pending.keys()]) flushKey(base, key);
}

function createStringStorage(base: Storage) {
  // Ensure unflushed writes survive navigation
  if (typeof window !== "undefined") {
    window.addEventListener("beforeunload", () => flushAll(base));
    window.addEventListener("pagehide", () => flushAll(base));
  }

  return {
    getItem: (key: string): string | null => {
      const buffered = pending.get(key);
      if (buffered) return buffered.value;
      return base.getItem(key);
    },
    setItem: (key: string, value: string): void => {
      const existing = pending.get(key);
      if (existing) clearTimeout(existing.timer);
      const timer = setTimeout(() => flushKey(base, key), DEBOUNCE_MS);
      pending.set(key, { timer, value });
    },
    removeItem: (key: string): void => {
      const existing = pending.get(key);
      if (existing) clearTimeout(existing.timer);
      pending.delete(key);
      base.removeItem(key);
    },
  };
}

const _stringStorageInstance =
  typeof window !== "undefined"
    ? createStringStorage(window.localStorage)
    : null;

const _ssrFallback = {
  getItem: (): string | null => null,
  setItem: (): void => {},
  removeItem: (): void => {},
};

/**
 * Factory : retourne un `SyncStorage<T>` debounce-backed pour un atomWithStorage.
 *
 * Usage :
 *   const myAtom = atomWithStorage(
 *     "my:key",
 *     initialValue,
 *     debouncedStorageFor<MyType>()
 *   );
 */
export function debouncedStorageFor<T>() {
  return createJSONStorage<T>(() => _stringStorageInstance ?? _ssrFallback);
}
